import { NgModule, isDevMode, NO_ERRORS_SCHEMA, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { HTTP_INTERCEPTORS, HttpClient, HttpClientJsonpModule, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpErrorInterceptor } from './services/http-error.interceptor';

import config from 'devextreme/core/config';
import { baseZIndex } from 'devextreme/ui/overlay';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MissingTranslationHandler, MissingTranslationHandlerParams, TranslateModule,
  TranslateService
} from '@ngx-translate/core';
import { OAuthModule } from 'angular-oauth2-oidc';
import { environment } from 'src/environments/environment';
import { AuthService } from './services/auth.service';

// Cargar localizaciones Bryntum dinámicamente
import { loadBryntumLocalizations } from './libs/utils/bryntum.helper';
import localeCa from '@angular/common/locales/ca';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeEu from '@angular/common/locales/eu';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';

registerLocaleData(localeEs);
registerLocaleData(localeEn);
registerLocaleData(localeCa);
registerLocaleData(localeDe);
registerLocaleData(localeIt);
registerLocaleData(localeFr);
registerLocaleData(localePt);
registerLocaleData(localeEu);

import { registerLocaleData } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  DxButtonModule, DxChartModule, DxDateBoxModule, DxDrawerModule,
  DxGalleryModule, DxListModule, DxLoadPanelModule, DxLookupComponent, DxMenuModule,
  DxNumberBoxModule,
  DxPopupModule, DxScrollViewModule, DxSelectBoxModule, DxTabPanelModule, DxTemplateModule,
  DxTextBoxModule, DxTooltipModule,
} from 'devextreme-angular';
import { NgPipesModule } from 'ngx-pipes';
import { firstValueFrom, forkJoin, tap } from 'rxjs';
import { GlobalSearchResultItemComponent } from './components/navigation/global-search/global-search-result-item/global-search-result-item.component';
import { GlobalSearchResultComponent } from './components/navigation/global-search/global-search-result/global-search-result.component';
import { GlobalSearchComponent } from './components/navigation/global-search/global-search.component';
import { MainMenuPanelComponent } from './components/navigation/main-menu-panel/main-menu-panel.component';
import { TestComponent } from './components/test/test.component';
import { GSModule } from './libs/gs.module';
import { TranslationsVersion } from './models';
import { CommonComponent } from './routes/common/common.component';
import { ClockingInTerminalModule } from './routes/hr/hr-operative/hr-attendance-entries/components/clocking-in-terminal/clocking-in-terminal.module';
import { PlanningComponent } from './routes/common/planning/planning.component';
import { LoginComponent } from './routes/login/login.component';
import { NotFoundComponent } from './routes/not-found/not-found.component';
import { ResetPasswordComponent } from './routes/reset-password/reset-password.component';
import { PlannerComponent } from './routes/common/planner/planner.component';
import { PlannerDetailComponent } from './routes/common/planner/planner-detail/planner-detail.component';
import { isSchedulerEnabled } from './libs/utils/bryntum.helper';

// Imports condicionales de módulos Bryntum
let BryntumCoreModule: any = null;
let BryntumSchedulerModule: any = null;

if (isSchedulerEnabled()) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const coreModule = require('@bryntum/core-angular-thin');
    BryntumCoreModule = coreModule.BryntumCoreModule;
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const schedulerModule = require('@bryntum/scheduler-angular-thin');
    BryntumSchedulerModule = schedulerModule.BryntumSchedulerModule;
  } catch (error) {
    console.warn('Bryntum modules not available', error);
  }
}

config({
  defaultCurrency: 'EUR',
  // editorStylingMode: 'underlined',
});

baseZIndex(1500);

function appInitializer(authService: AuthService, tr: TranslateService, http: HttpClient) {
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': 'en',
  }
  return () => new Promise(async resolve => {
    try {
      // Cargar localizaciones Bryntum si están habilitadas
      const currentLang = tr.currentLang || environment.defaultLanguage;
      await loadBryntumLocalizations(
        currentLang as 'ca' | 'es' | 'en'
      );
      await firstValueFrom(forkJoin([  // load translations and init auth service
        http.get<TranslationsVersion>(environment.api.ocelot + '/translations/version', { headers }).pipe(tap(rs => {
          const langIds: { [key: string]: string } = { 1: 'en' };
          const langs = Object.values(langIds);
          const messages: { [key: string]: { [key: string]: string } } = {};
          langs.forEach(l => messages[l] = {});
          rs.translations.forEach(t => messages[langIds[1]!][t.key] = t.value);
          tr.addLangs(langs);
          langs.forEach(l => tr.setTranslation(l, messages[l]!));
        })),
        authService.init()
      ]));
    } finally {
      resolve(null);
    }
  });
}

/**
* Quan no hi ha traducció en anglès, simplement pren la última part de la clau.
* Quan no hi ha traducció en castellà, mostra un warning a la consola.
*
* Exemple clau fallida en anglès:
* 'wms.entity.location.containerTypes' => 'container types'
* 'common.cancel' => 'cancel'
*/
class GlamMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    console.log('MISSING TRANSLATION: ' + params.key);
    //if (params.translateService.currentLang === 'en') {
    const str = params.key.lastIndexOf('.') > 0 ? params.key.substring(params.key.lastIndexOf('.') + 1) : params.key;
    const words = str.match(/[A-Za-z][a-z]*/g) || [];
    return words.map(w => w.toLocaleLowerCase()).join(' ');
    //}

    return '';
  }
}

@NgModule({
  declarations: [
    AppComponent,
    GlobalSearchComponent,
    GlobalSearchResultItemComponent,
    GlobalSearchResultComponent,
    MainMenuPanelComponent,
    LoginComponent,
    NotFoundComponent,
    TestComponent,
    ResetPasswordComponent,
    PlanningComponent,
    CommonComponent,
    PlannerComponent,
    PlannerDetailComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ClockingInTerminalModule,
    FormsModule,
    GSModule,
    ReactiveFormsModule,
    NgPipesModule,
    OAuthModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: environment.defaultLanguage,
      missingTranslationHandler: {provide: MissingTranslationHandler, useClass: GlamMissingTranslationHandler},
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    HttpClientJsonpModule,
    // BRYNTUM - módulos cargados dinámicamente
    ...(BryntumCoreModule ? [BryntumCoreModule] : []),
    ...(BryntumSchedulerModule ? [BryntumSchedulerModule] : []),
    // DEVEXTREME
    DxButtonModule,
    DxTemplateModule,
    DxTextBoxModule,
    DxDateBoxModule,
    DxLoadPanelModule,
    DxMenuModule,
    DxDrawerModule,
    DxTooltipModule,
    DxPopupModule,
    DxSelectBoxModule,
    DxNumberBoxModule,
    DxListModule,
    DxGalleryModule,
    DxScrollViewModule,
    DxChartModule,
    DxTabPanelModule,
    DxLookupComponent,
  ],
  providers: [
    provideAppInitializer(() => {
        const initializerFn = (appInitializer)(inject(AuthService), inject(TranslateService), inject(HttpClient));
        return initializerFn();
      }), {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
  ],
  schemas: [NO_ERRORS_SCHEMA]
})
export class AppModule { }
